"use client";

import React, { useState, useEffect } from "react";
import { Phone, Mail, MapPin, Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="78298" className="flex gap-4">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-6 w-20 animate-pulse rounded-md bg-muted" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-2 lg:gap-6">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative px-2 py-2 text-base font-medium font-sans text-foreground transition-colors hover:text-primary lg:px-4">
                {item.label}
                <span className="absolute bottom-0 left-2 right-2 h-[2px] origin-left scale-x-0 bg-primary transition-transform duration-300 ease-out group-hover:scale-x-100 lg:left-4 lg:right-4" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onNavigate
}: {
  onNavigate: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 p-4">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-8 w-32 animate-pulse rounded-md bg-muted" />)}
      </div>;
  }
  return <nav className="flex flex-col py-4">
      {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} onClick={onNavigate} data-index={i} className="px-6 py-4 text-lg font-medium font-sans text-foreground transition-colors hover:bg-muted hover:text-primary">
          {item.label}
        </Link>)}
    </nav>;
}
export default function MainMenu() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="header" className="sticky top-0 z-50 flex w-full flex-col">
      {}
      <div className={`flex items-center bg-secondary text-secondary-foreground transition-all duration-300 overflow-hidden ${isScrolled ? "h-0 opacity-0" : "h-12 opacity-100"}`}>
        <div className="container mx-auto flex max-w-7xl items-center justify-between px-4 !bg-[url(https://blessed-doberman-1.10web.cloud/wp-content/uploads/2026/04/TeOsfJ8-600x600-1.webp)]">
          <div className="hidden items-center gap-6 md:flex">
            <Link to="tel:0555222413" className="flex items-center gap-2 text-sm font-sans transition-colors hover:text-primary">
              <Phone className="h-4 w-4" />
              <span>055 5222413</span>
            </Link>
            <Link to="mailto:info@vipcompany.nl" className="flex items-center gap-2 text-sm font-sans transition-colors hover:text-primary">
              <Mail className="h-4 w-4" />
              <span>info@vipcompany.nl</span>
            </Link>
          </div>
          <div className="flex w-full items-center justify-center gap-4 text-sm font-sans md:w-auto md:justify-end">
            <MapPin className="h-4 w-4" />
            <span>Hoofdweg 215, Loenen (Gelderland)</span>
          </div>
        </div>
      </div>

      {}
      <header className={`flex h-20 w-full items-center transition-all duration-300 ${isScrolled ? "bg-background/80 backdrop-blur-md border-b border-border shadow-sm" : "bg-background"}`}>
        <div className="container mx-auto flex max-w-7xl items-center justify-between px-4">
          {}
          <Link to="/HomePage" className="flex items-center z-50">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1620688/img/image_68di1blv.webp" alt="VIP Company Logo" className="object-contain !w-[150px] !h-[150px] !p-[26px] !m-[35px]" />
          </Link>

          {}
          <div className="hidden items-center gap-8 lg:flex">
            <MenuProvider menu_id="46">
              <DesktopMenuConsumer />
            </MenuProvider>
            <Button className="rounded-lg bg-primary px-6 py-5 font-sans text-base font-medium uppercase tracking-wider text-primary-foreground shadow-sm transition-all hover:bg-primary/90 hover:shadow-md">
              AFSPRAAK
            </Button>
          </div>

          {}
          <Button variant="ghost" size="icon" className="z-50 flex items-center justify-center text-foreground transition-colors hover:text-primary lg:hidden" onClick={() => setIsMobileOpen(!isMobileOpen)} aria-label="Toggle menu">
            {isMobileOpen ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
          </Button>
        </div>
      </header>

      {}
      <div className={`absolute left-0 top-full w-full origin-top flex-col border-b border-border bg-background shadow-xl transition-all duration-300 ease-in-out lg:hidden ${isMobileOpen ? "scale-y-100 opacity-100 visible" : "scale-y-0 opacity-0 invisible"}`}>
        <MenuProvider menu_id="46">
          <MobileMenuConsumer onNavigate={() => setIsMobileOpen(false)} />
        </MenuProvider>
        <div className="border-t border-border p-6">
          <Button className="w-full rounded-lg bg-primary py-6 font-sans text-lg font-medium uppercase tracking-wider text-primary-foreground shadow-sm">
            AFSPRAAK
          </Button>
        </div>
      </div>
    </section>;
}